import { makeStyles } from '@material-ui/core/styles';
import {
  RELATION_HAS_PART,
  parseEntityRef,
  type Entity,
} from '@backstage/catalog-model';
import { Progress } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import {
  EntityInfoCard,
  EntityRefLink,
  catalogApiRef,
  useEntity,
} from '@backstage/plugin-catalog-react';
import useAsync from 'react-use/esm/useAsync';

/** Refs of the entities the given entity has as parts, optionally limited to some kinds. */
export function partRefsOf(entity: Entity, kinds?: string[]): string[] {
  return (entity.relations ?? [])
    .filter(
      r =>
        r.type === RELATION_HAS_PART &&
        (!kinds ||
          kinds.includes(
            parseEntityRef(r.targetRef).kind.toLocaleLowerCase('en-US'),
          )),
    )
    .map(r => r.targetRef);
}

/** Whether the entity is of the given kind. */
export const isKind = (entity: Entity, kind: string): boolean =>
  entity.kind.toLocaleLowerCase('en-US') === kind;

const useStyles = makeStyles({
  list: {
    display: 'flex',
    flexDirection: 'column',
    margin: 0,
    padding: 0,
    listStyle: 'none',
  },
  row: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    padding: '10px 0',
    borderTop: '1px solid var(--hone-portal-hairline-soft)',
    '&:first-child': { borderTop: 'none', paddingTop: 0 },
  },
  name: {
    fontFamily: 'var(--bui-font-monospace)',
    fontSize: 13,
    fontWeight: 500,
  },
  description: {
    fontSize: 12.5,
    lineHeight: 1.5,
    color: 'var(--bui-fg-secondary)',
  },
  empty: {
    fontSize: 12.5,
    color: 'var(--bui-fg-secondary)',
  },
});

export interface PartsCardProps {
  title: string;
  /** Lower-cased kinds to list; all `hasPart` targets when omitted. */
  kinds?: string[];
  emptyText: string;
  /** Prefix each description with the part's kind. */
  showKind?: boolean;
}

/** The entities the current entity has as parts, with each description. */
export function PartsCard({
  title,
  kinds,
  emptyText,
  showKind,
}: PartsCardProps) {
  const classes = useStyles();
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);
  const refs = partRefsOf(entity, kinds);

  const { value: parts, loading } = useAsync(async () => {
    if (refs.length === 0) return [];
    const { items } = await catalogApi.getEntitiesByRefs({
      entityRefs: refs,
      fields: [
        'kind',
        'metadata.name',
        'metadata.namespace',
        'metadata.title',
        'metadata.description',
      ],
    });
    return items.filter((item): item is Entity => Boolean(item));
  }, [catalogApi, refs.join(',')]);

  return (
    <EntityInfoCard title={title}>
      {loading && <Progress />}
      {!loading && (parts ?? []).length === 0 && (
        <span className={classes.empty}>{emptyText}</span>
      )}
      {!loading && (parts ?? []).length > 0 && (
        <ul className={classes.list}>
          {(parts ?? []).map(part => (
            <li
              className={classes.row}
              key={`${part.kind}:${part.metadata.name}`}
            >
              <span className={classes.name}>
                <EntityRefLink entityRef={part} />
              </span>
              {(showKind || part.metadata.description) && (
                <span className={classes.description}>
                  {[showKind && part.kind, part.metadata.description]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </EntityInfoCard>
  );
}
